import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Formik, FormikErrors, FormikTouched } from 'formik';
import { X } from 'phosphor-react';
import { CmsProduct, CmsVirtualProduct, ShopContent } from '../../../../types/cms_shared_types';
import { Conditional } from '../../../Common/Conditional';
import { Modal } from '../../Modal';
import { Product, ProductListActions } from '../../../../types/webshop';
import { useResetConfirmationModal } from '../../../../contexts/ResetConfirmationModalContext';
import { ConfirmationDialog } from '../../ConfirmationDialog';
import { getProductFromConfiguration } from './utils';
import { getInitialValues } from './initialValues';
import { getValidationSchema } from './validationSchema';
import { ProductConfigurationContent } from './ProductConfigurationContent';
import { ProductConfigurationActions } from './ProductConfigurationActions';

export enum MusicClass {
  A = 'A',
  B = 'B',
}

export interface ProductConfigurationInputs {
  measureInput: string;
  customMeasureInputSelected: boolean;
  customMeasureInput: string;
  timeMeasureInput: string;
  customTimeMeasureInputSelected: boolean;
  customTimeMeasureInput: string;
  musicClassToggleInput: boolean;
  startDate?: string;
  endDate?: string;
  _timeMeasureInputSelection?: string;
  isSeasonallyOpen: boolean;
}

export interface ProductConfigurationModalFormValues extends ProductConfigurationInputs {
  productValues?: Record<string, ProductConfigurationInputs>;
}

interface ProductConfigurationModalProps {
  cmsProduct: CmsProduct;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedProducts: Product[];
  selectedProductsActions: ProductListActions;
  cmsShopContent: ShopContent;
  lastProductResetConfirmation?: boolean;
  monthlyInvoicedAddonsInCart?: boolean;
  removeAddons?: () => void;
  virtualProduct?: CmsVirtualProduct;
}

export const ProductConfigurationModal: FC<ProductConfigurationModalProps> = ({
  cmsProduct,
  showModal,
  setShowModal,
  selectedProducts,
  selectedProductsActions,
  cmsShopContent,
  lastProductResetConfirmation = true,
  monthlyInvoicedAddonsInCart,
  removeAddons,
  virtualProduct,
}) => {
  const isVirtualProduct = virtualProduct !== undefined;
  const [fetchingProduct, setFetchingProduct] = useState(false);
  const [showRemoveAddonsModal, setShowRemoveAddonsModal] = useState(false);
  const [skippedProductCodes, setSkippedProductCodes] = useState<Set<string>>(new Set());

  // Reset skipped products when modal opens
  useEffect(() => {
    if (showModal) {
      setSkippedProductCodes(new Set());
    }
  }, [showModal]);

  // Close modal when all products are skipped
  useEffect(() => {
    if (isVirtualProduct && skippedProductCodes.size > 0) {
      const allProductsSkipped = virtualProduct.products.every((product) => skippedProductCodes.has(product.code));
      if (allProductsSkipped) {
        setShowModal(false);
      }
    }
  }, [skippedProductCodes, isVirtualProduct, virtualProduct, setShowModal]);

  const selectedProduct = useMemo(
    () => (isVirtualProduct ? undefined : selectedProducts.find((product) => product.code === cmsProduct.code)),
    [isVirtualProduct, selectedProducts, cmsProduct],
  );
  const isEditMode = useMemo(() => {
    if (!isVirtualProduct) {
      return selectedProduct !== undefined;
    }

    return virtualProduct.products.some((product) =>
      selectedProducts.some((selected) => selected.code === product.code),
    );
  }, [isVirtualProduct, selectedProduct, selectedProducts, virtualProduct]);

  const validationSchema = useMemo(
    () => getValidationSchema(cmsProduct, cmsShopContent, virtualProduct, skippedProductCodes),
    [cmsProduct, cmsShopContent, virtualProduct, skippedProductCodes],
  );
  const initialValues = useMemo(
    () => getInitialValues(cmsProduct, selectedProduct, virtualProduct, selectedProducts),
    [cmsProduct, selectedProduct, virtualProduct, selectedProducts],
  );

  const initialTouched = useMemo(() => {
    if (!isVirtualProduct) {
      return { startDate: true, endDate: true };
    }

    const productTouched: Record<string, { startDate: boolean; endDate: boolean }> = Object.fromEntries(
      virtualProduct.products.map((product) => [product.code, { startDate: true, endDate: true }]),
    );

    return {
      startDate: true,
      endDate: true,
      productValues: productTouched as unknown,
    } as FormikTouched<ProductConfigurationModalFormValues>;
  }, [isVirtualProduct, virtualProduct]);

  const { openResetConfirmationModal } = useResetConfirmationModal();

  const valuesChanged = useCallback(
    (values: ProductConfigurationModalFormValues) => {
      const formValuesChanged = JSON.stringify(initialValues) !== JSON.stringify(values);

      // In edit mode for virtual products, check if any products in cart have been skipped
      if (isEditMode && isVirtualProduct && skippedProductCodes.size > 0) {
        const hasSkippedProductsInCart = Array.from(skippedProductCodes).some((code) =>
          selectedProducts.some((p) => p.code === code),
        );
        if (hasSkippedProductsInCart) {
          return true;
        }
      }

      return formValuesChanged;
    },
    [initialValues, isEditMode, isVirtualProduct, skippedProductCodes, selectedProducts],
  );

  const addProduct = async (values: ProductConfigurationModalFormValues) => {
    if (!cmsProduct.isSchablon) {
      setFetchingProduct(true);
    }

    try {
      if (isVirtualProduct) {
        const productsToAdd = virtualProduct.products.filter((p) => !skippedProductCodes.has(p.code));

        // If all products are skipped, just close the modal without adding anything
        if (productsToAdd.length === 0) {
          setShowModal(false);
          return;
        }

        const newProducts = await Promise.all(
          productsToAdd.map((product) => {
            const productValues =
              values.productValues?.[product.code] ?? initialValues.productValues?.[product.code] ?? initialValues;
            return getProductFromConfiguration(product, productValues);
          }),
        );
        selectedProductsActions.addMultiple(newProducts);
      } else {
        const newProduct = await getProductFromConfiguration(cmsProduct, values);
        selectedProductsActions.add(newProduct);
      }
    } finally {
      setFetchingProduct(false);
      setShowModal(false);
    }
  };

  const editProduct = async (values: ProductConfigurationModalFormValues) => {
    if (!cmsProduct.isSchablon) {
      setFetchingProduct(true);
    }

    try {
      if (isVirtualProduct) {
        for (const product of virtualProduct.products) {
          // Skip this product if it's marked as skipped
          if (skippedProductCodes.has(product.code)) {
            continue;
          }

          const selectedVirtualProduct = selectedProducts.find((selected) => selected.code === product.code);
          const productValues =
            values.productValues?.[product.code] ?? initialValues.productValues?.[product.code] ?? initialValues;
          const newProduct = await getProductFromConfiguration(product, productValues, selectedVirtualProduct);
          selectedProductsActions.replace(newProduct);
        }

        // Remove products that are now skipped
        const skippedProductsInCart = Array.from(skippedProductCodes).filter((code) =>
          selectedProducts.some((p) => p.code === code),
        );
        if (skippedProductsInCart.length > 0) {
          selectedProductsActions.removeByCodes(skippedProductsInCart);
        }
      } else {
        const newProduct = await getProductFromConfiguration(cmsProduct, values);
        selectedProductsActions.replace(newProduct);
      }
    } finally {
      setFetchingProduct(false);
      setShowModal(false);
    }
  };

  const removeProduct = () => {
    if (isVirtualProduct) {
      const virtualProductCodes = virtualProduct.products.map((p) => p.code);
      const remainingProductsCount = selectedProducts.filter((p) => !virtualProductCodes.includes(p.code)).length;

      if (remainingProductsCount === 0 && lastProductResetConfirmation) {
        openResetConfirmationModal();
      } else {
        selectedProductsActions.removeByCodes(virtualProductCodes);
        setShowModal(false);
      }
    } else {
      if (selectedProducts.length === 1 && lastProductResetConfirmation) {
        openResetConfirmationModal();
      } else {
        const prodIndex = selectedProducts.findIndex((p) => p.code === cmsProduct.code);
        selectedProductsActions.remove(prodIndex);
        setShowModal(false);
      }
    }
  };

  return (
    <Formik
      onSubmit={(values) => {
        if (isEditMode) {
          editProduct(values);
        } else {
          addProduct(values);
        }
      }}
      initialValues={initialValues}
      initialTouched={initialTouched}
      validationSchema={validationSchema}
      validateOnMount
      enableReinitialize={true}
    >
      {({ isValid, submitForm, values, errors, validateForm }) => {
        const productContents = isVirtualProduct ? (
          <div className="flex flex-col gap-8">
            {virtualProduct.products.map((product) => {
              const isSkipped = skippedProductCodes.has(product.code);
              const productValues =
                values.productValues?.[product.code] ?? initialValues.productValues?.[product.code] ?? initialValues;
              const productErrors = ((
                errors.productValues as Record<string, FormikErrors<ProductConfigurationInputs>> | undefined
              )?.[product.code] ?? {}) as FormikErrors<ProductConfigurationInputs>;

              return (
                <Conditional key={product.code} condition={!isSkipped}>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <div className="text-lg font-semibold">{product.name}</div>
                      <button
                        type="button"
                        onClick={() => {
                          const newSkipped = new Set(skippedProductCodes);
                          if (isSkipped) {
                            newSkipped.delete(product.code);
                          } else {
                            newSkipped.add(product.code);
                          }
                          setSkippedProductCodes(newSkipped);
                          // Revalidate after state update
                          setTimeout(() => validateForm(), 0);
                        }}
                        className="text-sami-dark-gray hover:text-sami-red transition-colors"
                        title="Skip this product"
                      >
                        <X size={24} />
                      </button>
                    </div>
                    <Conditional condition={!!product.description}>
                      <div className="text-sm text-sami-dark-gray">{product.description}</div>
                    </Conditional>
                    <ProductConfigurationContent
                      cmsProduct={product}
                      cmsShopContent={cmsShopContent}
                      loadingPrice={fetchingProduct}
                      values={productValues}
                      errors={productErrors}
                      fieldPrefix={`productValues.${product.code}`}
                    />
                  </div>
                </Conditional>
              );
            })}
          </div>
        ) : (
          <ProductConfigurationContent
            cmsProduct={cmsProduct}
            cmsShopContent={cmsShopContent}
            loadingPrice={fetchingProduct}
            values={values}
            errors={errors as FormikErrors<ProductConfigurationInputs>}
          />
        );

        return (
          <>
            <Modal
              showModal={showModal}
              setShowModal={setShowModal}
              header={<div>{isVirtualProduct ? virtualProduct.name : cmsProduct.name}</div>}
              description={
                <Conditional condition={!fetchingProduct}>
                  <div>{isVirtualProduct ? virtualProduct.description : cmsProduct.description}</div>
                </Conditional>
              }
              outsideClickCloses={!fetchingProduct}
              content={productContents}
              actions={
                <ProductConfigurationActions
                  onSubmit={() => {
                    if (!monthlyInvoicedAddonsInCart || !cmsProduct.reporting || isEditMode) {
                      submitForm();
                    } else {
                      setShowRemoveAddonsModal(true);
                    }
                  }}
                  loadingPrice={fetchingProduct}
                  cmsShopContent={cmsShopContent}
                  editMode={isEditMode}
                  product={cmsProduct}
                  isValid={isValid}
                  removeProduct={removeProduct}
                  valuesChanged={valuesChanged(values)}
                />
              }
            />
            {removeAddons && (
              <ConfirmationDialog
                title={cmsShopContent.productChoiceStep.removeAddonsTitle}
                description={cmsShopContent.productChoiceStep.removeAddonsDescription}
                cmsContent={cmsShopContent}
                setShowModal={setShowRemoveAddonsModal}
                showModal={showRemoveAddonsModal}
                onYesCallback={() => {
                  submitForm();
                  removeAddons();
                }}
              />
            )}
          </>
        );
      }}
    </Formik>
  );
};
